'use client';
import { useEffect, useRef, type ReactNode } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useSession } from '@/lib/auth-client';

/**
 * Cross-tab session synchronization.
 *
 * Better Auth (1.6.x) broadcasts SIGN-OUT across tabs but does NOT broadcast
 * SIGN-IN, and nothing in the app drives navigation in the non-acting tabs. So a
 * tab can keep showing the logged-out UI until a manual refresh.
 *
 * This module closes the gaps with three mechanisms:
 *  1. `publishSignIn` / `publishSignOut` notify every other tab (BroadcastChannel
 *     + a localStorage write) so they refetch their session immediately.
 *  2. `SessionSyncProvider` refetches the shared Better Auth session store on those
 *     messages AND whenever the tab regains focus/visibility — so switching to a
 *     stale tab is enough to pick up the new state.
 *  3. It redirects an authenticated tab away from the auth-entry pages; protected
 *     `(app)` routes already redirect to `/login` on a null session, and the forced
 *     refetch makes that fire in real time.
 */

const CHANNEL_NAME = 'scoper-session-sync';
const STORAGE_KEY = 'scoper:session-sync';
// Collapse the focus + visibilitychange + message events that often fire together
// into a single refetch.
const REFETCH_THROTTLE_MS = 800;

type AuthChangeKind = 'signin' | 'signout';

// URL paths (route groups don't appear in the URL) where an authenticated user
// should be bounced to the dashboard. Kept to the unambiguous entry pages so we
// don't interrupt multi-step flows like /two-factor or /reset-password.
const AUTH_ENTRY_PATHS = new Set<string>(['/login', '/register', '/forgot-password']);

// One channel per tab, kept open for the tab's lifetime. Creating a channel,
// posting, then immediately close()-ing can drop the message before it is
// delivered, so we never close it eagerly.
let sharedChannel: BroadcastChannel | null = null;
let channelUnavailable = false;

function getChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || channelUnavailable) return null;
  if (!sharedChannel) {
    try {
      sharedChannel = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      channelUnavailable = true;
      sharedChannel = null;
    }
  }
  return sharedChannel;
}

function postAuthChange(kind: AuthChangeKind): void {
  if (typeof window === 'undefined') return;
  const payload = JSON.stringify({ kind, at: Date.now() });
  getChannel()?.postMessage(payload);
  // Also write to localStorage: it fires a `storage` event in other tabs and acts
  // as a fallback where BroadcastChannel is unavailable. Refetch is idempotent, so
  // receiving both signals is harmless.
  try {
    window.localStorage.setItem(STORAGE_KEY, payload);
  } catch {
    // ignore — sync is best-effort
  }
}

export function publishSignIn(): void {
  postAuthChange('signin');
}

export function publishSignOut(): void {
  postAuthChange('signout');
}

export function SessionSyncProvider({ children }: { children: ReactNode }): ReactNode {
  const router = useRouter();
  const pathname = usePathname();
  const { data: session, isPending, refetch } = useSession();

  // `refetch` identity can change between renders; keep the latest in a ref so the
  // listener effect can stay mounted for the tab's lifetime without re-binding.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const lastRefetchRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const refetchSession = (): void => {
      const now = Date.now();
      if (now - lastRefetchRef.current < REFETCH_THROTTLE_MS) return;
      lastRefetchRef.current = now;
      refetchRef.current();
    };

    const channel = getChannel();
    const onMessage = (): void => refetchSession();
    if (channel) channel.addEventListener('message', onMessage);

    const onStorage = (event: StorageEvent): void => {
      if (event.key === STORAGE_KEY && event.newValue) refetchSession();
    };
    window.addEventListener('storage', onStorage);

    // Switching to a stale tab should be enough to pick up a session change made
    // elsewhere, even if the cross-tab message was missed.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') refetchSession();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      if (channel) channel.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  // When a tab learns it is now authenticated (e.g. the user logged in elsewhere)
  // but is still sitting on an auth-entry page, move it to the dashboard.
  useEffect(() => {
    if (isPending) return;
    if (session && AUTH_ENTRY_PATHS.has(pathname)) {
      router.replace('/dashboard');
    }
  }, [session, isPending, pathname, router]);

  return children;
}
